import { PrimitiveAtom, atom } from 'jotai';
import { atomFamily } from 'jotai/utils';

import { FormItemAtom } from './types/form';

/**
 * List of forms atom id's
 */
const formyListAtom = atom<FormItemAtom['id'][]>([]);
formyListAtom.debugLabel = 'formyListAtom';

/**
 * Form atom family
 * This is a map of all the forms that are being validated
 */
const formyAtomFamily = atomFamily(
  ({ id }: { id: string }): PrimitiveAtom<Partial<FormItemAtom>> => {
    const newAtom = atom<Partial<FormItemAtom>>({ id });
    newAtom.debugLabel = `formyAtomFamily: ${id}`;
    return newAtom;
  },
  (aItem, bItem) => aItem.id === bItem.id
);

/**
 * List of all form elements mapped from their id's to their atoms
 */
const formyFormsAtoms = atom((get) => {
  const list = get(formyListAtom);
  return list.map((id) => get(formyAtomFamily({ id })));
});
formyFormsAtoms.debugLabel = 'formyFormsAtoms';

/**
 * Map of all forms
 */
const formFormMapAtom = atom((get) => {
  const validatorList = get(formyListAtom);
  const list = {} as Record<string, Partial<FormItemAtom>>;
  validatorList.forEach((id) => {
    const form = get(formyAtomFamily({ id }));
    const formId = form.id || id;
    Object.assign(list, { [formId]: form });
  });

  return list;
});
formFormMapAtom.debugLabel = 'formyListMapAtom';

/**
 * An atom that returns a form by the id
 * */
const formByIdAtom = atom(null, (get, _, id: string | number | symbol) => {
  const list = get(formyFormsAtoms);
  return list.find((form) => form.id === id);
});
formByIdAtom.debugLabel = 'formByIdAtom';
/**
 *
 * Utils
 *
              _
             | |
             | |===( )   //////
             |_|   |||  | o o|
                    ||| ( c  )                  ____
                     ||| \= /                  ||   \_
                      ||||||                   ||     |
                      ||||||                ...||__/|-"
                      ||||||             __|________|__
                        |||             |______________|
                        |||             || ||      || ||
                        |||             || ||      || ||
------------------------|||-------------||-||------||-||-------
                        |__>            || ||      || ||


     hit any key to continue
 */

/**
 * Checks if all forms are valid
 */
const isAllFormsValidAtom = atom((get) => {
  const list = get(formyFormsAtoms);
  const allFormsValidStatus = list.map((form) => form?.isValid?.());
  const hasError = allFormsValidStatus.some((formValidState) => formValidState === false);

  return !hasError;
});

/** Get the errored forms */

const getErroredFormsAtomsAtom = atom(null, (get) => {
  const list = get(formyFormsAtoms);

  return list.filter((form) => form?.isValid?.() == false);
});
getErroredFormsAtomsAtom.debugLabel = 'getErroredFormsAtomsAtom';
/**
 * Checks if any forms are dirty
 */
const isAnyFormDirtyAtom = atom((get) => {
  const list = get(formyFormsAtoms);
  return list.some((form) => form?.isDirty?.());
});

/**
 * Calls a method on all forms
 *
 * This can be used to execute a method on all forms in the list
 */
// Explicitly type the atom to avoid reference to node_modules
// Use a string type instead of keyof Form to avoid the reference issue
const FormsMethodAtom = atom<null, [string], void>(null, (get, _, formMethod: string) => {
  const list = get(formyFormsAtoms);
  list.forEach((form) => {
    const formFunction = form?.[formMethod as keyof typeof form];
    if (formMethod in form) {
      if (typeof formFunction == 'function') {
        // @ts-expect-error -- TODO: Update Form God
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return formFunction?.();
      } else {
        return formFunction;
      }
    }
  });
});

/**
 * Validates all forms and then returns the list of forms
 */

const validateAllFormsAsyncAtom = atom(null, async (get) => {
  const list = get(formyFormsAtoms);
  const validations = list.map((form) => {
    const validation = form?.validate?.();
    return {
      form,
      ...validation,
    };
  });

  const validationResultsList = await Promise.all(validations);

  // Forms that contain errors
  const hasErrorsForms = validationResultsList.filter((form) => form?.hasErrors == true);

  const isAllFormsValid = hasErrorsForms.length == 0;

  return {
    /**
     * Whether all forms are valid
     */
    isValid: isAllFormsValid,
    /**
     * Forms that contain errors
     */
    erroredForms: hasErrorsForms,
    /**
     * All forms
     */
    forms: list,
  };
});

export {
  FormsMethodAtom,
  formByIdAtom,
  formFormMapAtom,
  formyAtomFamily,
  formyFormsAtoms,
  formyListAtom,
  getErroredFormsAtomsAtom,
  isAllFormsValidAtom,
  isAnyFormDirtyAtom,
  validateAllFormsAsyncAtom,
};
